<template>
  <section class="card sensor-status-card">
    <header class="card-hdr">
      <div class="title-group">
        <h3>Sensor status</h3>
        <div class="live-status">
          <span class="live-indicator" :class="{ 'is-delay': isDelay }">
            <span class="dot"></span> {{ isDelay ? "DELAY" : "LIVE" }}
          </span>
          <span v-if="formattedOverallTs" class="overall-ts" title="Ultimo valore disponibile">
           {{ formattedOverallTs }}
          </span>
        </div>
      </div>
      <div class="hdr-actions">
<!-- ... Slide through logic remains same ... -->
        <div class="search-overlay">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            placeholder="Cerca sensore..."
            @blur="onSearchBlur"
          />
        </div>
        <!-- <button class="ico-btn-card" @click="toggleSearch" title="Cerca">
          <FontAwesomeIcon :icon="faSearch" />
        </button> -->
        <button class="ico-btn-card" @click="fetchLatestData" :disabled="loading" title="Aggiorna">
          <FontAwesomeIcon :icon="faSync" :spin="loading" />
        </button>
      </div>
    </header>

    <div class="card-body scrollable">
      <div v-if="loading && !sensors.length" class="loading-state">
        <FontAwesomeIcon :icon="faSync" spin />
      </div>
      <div v-else-if="!filteredSensors.length" class="empty-state">
        {{ searchQuery ? 'Nessun match' : 'Nessun dato' }}
      </div>
      <ul v-else class="sensor-list">
        <li v-for="sensor in filteredSensors" :key="sensor.id" class="sensor-item">
          <div class="sensor-main">
            <span class="sensor-label" :title="sensor.id">{{ getLabelWithoutUnit(sensor.label) }}</span>
            <span class="sensor-value">{{ sensor.value }}<span v-if="getUnit(sensor.label)" class="sensor-unit"> {{ getUnit(sensor.label) }}</span></span>
          </div>
          <div class="sensor-meta">
            <span class="sensor-id">{{ sensor.id }}</span>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import axios from "@/axiosInstance";
import { SENSOR_LABELS, SENSOR_ORDER } from "@/config/chartConfig";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faSync, faSearch } from "@fortawesome/free-solid-svg-icons";

const props = defineProps<{
  deviceId: string | number;
}>();

const sensors = ref<any[]>([]);
const loading = ref(false);
const lastUpdate = ref<Date | null>(null);
const isDelay = ref(true);
const searchQuery = ref("");
const showSearch = ref(false);
const searchInput = ref<HTMLInputElement | null>(null);
let refreshInterval: any = null;

const formattedOverallTs = computed(() => {
  if (!lastUpdate.value) return "";
  const d = lastUpdate.value;
  
  // Format: YYYY-MM-DD HH:mm:ss (UTC to match charts)
  const pad = (n: number) => String(n).padStart(2, '0');
  const Y = d.getUTCFullYear();
  const M = pad(d.getUTCMonth() + 1);
  const D = pad(d.getUTCDate());
  const h = pad(d.getUTCHours());
  const m = pad(d.getUTCMinutes());
  const s = pad(d.getUTCSeconds());
  
  return `${Y}-${M}-${D} ${h}:${m}:${s}`;
});

const filteredSensors = computed(() => {
  let list = sensors.value;
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(s => 
      s.label.toLowerCase().includes(q) || 
      s.id.toLowerCase().includes(q)
    );
  }
  return list;
});

async function fetchLatestData() {
  if (!props.deviceId) return;
  loading.value = true;
  try {
    const response = await axios.get(`/records/devices/${props.deviceId}/measurements`, {
      params: {
        range: "24h",
        limit: 1000,
        sort: "desc"
      }
    });

    const data = response.data || [];
    processData(data);
  } catch (err) {
    console.error("[SensorStatusCard] Error fetching data:", err);
  } finally {
    loading.value = false;
  }
}

function processData(data: any[]) {
  const latestByEntity: Record<string, any> = {};
  let overallLastTs: number = 0;

  data.forEach((item) => {
    // Only include sensors with a label
    if (SENSOR_LABELS[item.entity_id]) {
      // Since data is now sorted DESC (newest first), the first occurrence of an entity is the latest one
      if (!latestByEntity[item.entity_id]) {
        const ts = new Date(item.ts).getTime();
        latestByEntity[item.entity_id] = {
          id: item.entity_id,
          label: SENSOR_LABELS[item.entity_id],
          value: item.value_num !== null ? item.value_num : item.value,
          ts: ts,
          formattedTs: new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
        };
        if (ts > overallLastTs) overallLastTs = ts;
      }
    }
  });

  // sensors.value = Object.values(latestByEntity).sort((a, b) => a.label.localeCompare(b.label));

  const orderIndex = new Map(SENSOR_ORDER.map((id, idx) => [id, idx]));

sensors.value = Object.values(latestByEntity).sort((a: any, b: any) => {
  const ai = orderIndex.has(a.id) ? orderIndex.get(a.id)! : Number.POSITIVE_INFINITY;
  const bi = orderIndex.has(b.id) ? orderIndex.get(b.id)! : Number.POSITIVE_INFINITY;

  // 1) prima quelli nella lista, rispettando l'ordine
  if (ai !== bi) return ai - bi;

  // 2) poi tutti gli altri: alfabetico per label (come facevi già)
  return String(a.label).localeCompare(String(b.label), "it", { sensitivity: "base" });
});

  
  if (overallLastTs > 0) {
    lastUpdate.value = new Date(overallLastTs);
    const now = Date.now();
    const threshold = 15 * 60 * 1000;
    isDelay.value = (now - overallLastTs) > threshold;
  }
}

function toggleSearch() {
  showSearch.value = !showSearch.value;
  if (showSearch.value) {
    nextTick(() => searchInput.value?.focus());
  } else {
    searchQuery.value = "";
  }
}

function onSearchBlur() {
  if (!searchQuery.value) showSearch.value = false;
}

function startPolling() {
  stopPolling();
  refreshInterval = setInterval(fetchLatestData, 120000);
}

function stopPolling() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
}

watch(() => props.deviceId, () => {
  fetchLatestData();
});

onMounted(() => {
  fetchLatestData();
  startPolling();
});

onBeforeUnmount(() => {
  stopPolling();
});
function getLabelWithoutUnit(label: string): string {
  if (!label) return "";
  return label.replace(/\s*\([^)]*\)$/, "").trim();
}

function getUnit(label: string): string {
  if (!label) return "";
  const match = label.match(/\(([^)]+)\)$/);
  return match ? match[1] : "";
}

</script>

<style scoped>
.sensor-status-card {
  display: flex;
  flex-direction: column;
  height: 330px; /* Fixed height to match LocationCard + Address */
  overflow: hidden;
  min-height: 0;
}

.card-body.scrollable {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.card-hdr {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.hdr-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-overlay {
  position: absolute;
  right: 50px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: #1a1a1a;
}

.search-overlay input {
  background: #0d0d0d;
  border: 1px solid #333;
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.8rem;
  width: 300px;
}

.search-overlay input:focus {
  outline: none;
  border-color: #00a7a7;
}

@media (max-width: 768px) {
  .search-overlay {
    right: 40px;
  }
  .search-overlay input {
    width: 150px;
  }
}

.live-status {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 12px;
}

.live-indicator {
  font-size: 0.65rem;
  font-weight: bold;
  color: #4caf50;
  display: flex;
  align-items: center;
  gap: 4px;
}

.live-indicator .dot {
  width: 6px;
  height: 6px;
  background-color: #4caf50;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.live-indicator.is-delay {
  color: #ff9800;
}

.live-indicator.is-delay .dot {
  background-color: #ff9800;
  animation: none;
  opacity: 0.8
}

.overall-ts {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.5);
  font-family: monospace;
}

.scrollable {
  overflow-y: auto;
  flex: 1;
  padding: 8px;
}

.sensor-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sensor-item {
  display: flex;
  flex-direction: column;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.2s;
}

.sensor-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.sensor-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.sensor-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #e0e0e0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.sensor-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #22c55e;
}

.sensor-unit {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
  margin-left: 3px;
}

.sensor-meta {
  display: flex;
  justify-content: space-between;
  margin-top: 1px;
}

.sensor-id {
  font-size: 0.6rem;
  color: rgba(106, 124, 141, 0.7);
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%;
}

.sensor-ts {
  font-size: 0.6rem;
  color: rgba(106, 124, 141, 0.7);
}

.loading-state, .empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6a7c8d;
  font-size: 0.85rem;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.3); }
  70% { box-shadow: 0 0 0 6px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

/* Scrollbar styling */
.scrollable::-webkit-scrollbar {
  width: 6px;
}
.scrollable::-webkit-scrollbar-track {
  background: transparent;
}
.scrollable::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}
.scrollable::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}
</style>
